// Topbar status pill: literal text answer to "is claude doing something
// right now?". Title-case labels — minimalistic, easy to scan on phone.
//   ● Working    — running + busy=true (a turn is in flight)
//   ● Ready      — running + busy=false (session alive between turns)
//   ● Starting…  — spawning
//   ● Stopping…  — killing
//   ● Error      — errored
//   ● Idle       — no server-side session record
//
// Polls `sessionStatusByCompany[activeCompanyId]` from the store, which the
// 6s `/all-status` poll keeps fresh. No SSE subscription needed here —
// status pill is intentionally a low-frequency indicator.

import { useEffect, useState } from "react";
import { useSession } from "../store/session";
import { helperCountsFromStatus } from "../lib/helperPresentation";
import { slotWaitLabel } from "../lib/slotWait";

// How long after a `done` event we keep showing "Ready". After this window
// the pill disappears entirely — alive-but-quiet sessions shouldn't have a
// permanent status display (Julian: "don't show Ready forever").
const READY_WINDOW_MS = 10_000;

export default function SessionStatusPill() {
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const sessionStatusByCompany = useSession((s) => s.sessionStatusByCompany);
  // Tick every 1s so the Ready window auto-expires without waiting for the
  // next /all-status poll (which is on a 6s cadence). Cheap setInterval.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!activeCompanyId) return null;
  const st = sessionStatusByCompany[activeCompanyId];
  // Helpers (background sub-agents) outlive the reply, so "Ready" would be a
  // lie while they run. Their own chip (HelpersChip) sits right after this pill.
  const helpers = helperCountsFromStatus(st);
  const helpersActive = !!helpers && helpers.running + helpers.needsYou > 0;

  // Always-on states (something is happening / went wrong) — show pill.
  if (st?.state === "spawning") {
    return (
      <span className="sessionStatusPill is-spawning" title="Claude is starting up — about 10s">
        <span className="sessionStatusPill__dot" />
        Starting…
      </span>
    );
  }
  if (st?.state === "killing") {
    return (
      <span className="sessionStatusPill is-stopping" title="Killing claude process">
        <span className="sessionStatusPill__dot" />
        Stopping…
      </span>
    );
  }
  // Waiting in line for a slot: the message is saved, nothing failed — never "Error".
  if (st?.waitingForSlot) {
    return (
      <span className="sessionStatusPill is-spawning" title="Every agent slot is busy. Your message is saved and sends by itself when one frees up.">
        <span className="sessionStatusPill__dot" />
        {slotWaitLabel(st.slotPlace)}
      </span>
    );
  }
  if (st?.state === "errored") {
    return (
      <span className="sessionStatusPill is-error" title={st.errorMessage || "Last spawn failed"}>
        <span className="sessionStatusPill__dot" />
        Error
      </span>
    );
  }
  if (st?.state === "running" && st.busy === true) {
    return (
      <span className="sessionStatusPill is-working" title="Claude is processing your message right now">
        <span className="sessionStatusPill__dot" />
        <span className="sessionStatusPill__text">Working</span>
      </span>
    );
  }
  // Working on its own after a helper / background job finished (no operator turn open).
  if (st?.state === "running" && st.autoTurn) {
    return (
      <span className="sessionStatusPill is-working" title="A helper finished — the agent is reading its results and will reply">
        <span className="sessionStatusPill__dot" />
        <span className="sessionStatusPill__text">Working</span>
      </span>
    );
  }
  // Helpers still working: the helpers chip says so; no "Ready" beside it.
  if (helpersActive) return null;

  // Transient state: running + !busy → show "Ready" briefly, then hide.
  // The pill should only celebrate the moment of completion, not persist.
  if (st?.state === "running" && st.busy === false) {
    const recent = st.lastDoneAt && Date.now() - st.lastDoneAt < READY_WINDOW_MS;
    if (recent) {
      return (
        <span className="sessionStatusPill is-ready" title="Claude just finished — next message goes straight in">
          <span className="sessionStatusPill__dot" />
          Ready
        </span>
      );
    }
    // Alive but quiet — render nothing. Less is more.
    return null;
  }

  // No server entry at all → render nothing. "Idle" was just visual noise;
  // the absence of a pill IS the idle signal.
  return null;
}
